import { usePageMeta } from "@/lib/pageMeta";
import { useState, useMemo, useEffect } from "react";
import { PortfolioStrip } from "@/components/dashboard/PortfolioStrip";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { usePortfolio } from "@/context/PortfolioContext";
import { usePrivacy } from "@/context/PrivacyContext";
import { TransactionDialog } from "@/components/TransactionDialog";
import { EmptyState } from "@/components/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtCurrency, fmtDate, fmtNumber } from "@/lib/format";
import { TXN_BADGE } from "@/lib/constants";
import { Plus, Search, Pencil, Trash2, Download, Upload, ArrowLeftRight, ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { ImportDialog } from "@/components/ImportDialog";

// Totals for the transactions currently listed (follow every filter)
function txnStripCells(list, mask) {
    const gross = (t) => (t.quantity || 0) * (t.price || 0);
    const of = (type) => list.filter((t) => (t.transaction_type || "").toUpperCase() === type);
    const sum = (arr, f) => arr.reduce((a, t) => a + f(t), 0);
    const today = new Date().toISOString().slice(0, 10);
    const buys = of("BUY"), sells = of("SELL"), divs = of("DIVIDEND").filter((t) => (t.transaction_date || "").slice(0, 10) <= today); // received = paid by today
    const cash = of("DEPOSIT").length + of("WITHDRAWAL").length;
    const divGross = sum(divs, gross), divTax = sum(divs, (t) => t.tax_withheld || 0);
    return [
        { testId: "tstat-count", label: "Transactions", value: list.length.toLocaleString(), sub: `${buys.length} buy · ${sells.length} sell · ${of("DIVIDEND").length} div${cash ? ` · ${cash} cash` : ""}` },
        { testId: "tstat-bought", label: "Total Bought", value: mask(fmtCurrency(sum(buys, gross))), sub: `${buys.length} buys` },
        { testId: "tstat-sold", label: "Total Sold", value: mask(fmtCurrency(sum(sells, gross))), sub: `${sells.length} sells` },
        { testId: "tstat-dividends", label: "Dividends Received (net)", value: mask(fmtCurrency(divGross - divTax)), sub: `${mask(fmtCurrency(divGross))} before tax` },
    ];
}

export default function Transactions() {
    const { selectedSub, setSelectedSub, subs, refreshSubs } = usePortfolio();
    const { mask } = usePrivacy();
    const qc = useQueryClient();
    const [filters, setFilters] = useState({ type: "ALL", ticker: "", search: "", start: "", end: "" });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const buildQ = () => {
        const p = new URLSearchParams();
        if (selectedSub !== "ALL") p.set("sub_portfolio", selectedSub);
        if (filters.ticker) p.set("ticker", filters.ticker);
        if (filters.search) p.set("search", filters.search);
        if (filters.start) p.set("start_date", filters.start);
        if (filters.end) p.set("end_date", filters.end);
        return p.toString();
    };

    const { data, isLoading, dataUpdatedAt } = useQuery({ queryKey: ["txns", selectedSub, filters], queryFn: async () => (await api.get(`/transactions?${buildQ()}`)).data });
    const refetch = () => qc.invalidateQueries();

    const [sort, setSort] = useState(() => {
        try { return JSON.parse(localStorage.getItem("txn-sort")) || { key: "date", dir: "desc" }; }
        catch { return { key: "date", dir: "desc" }; }
    });
    useEffect(() => { localStorage.setItem("txn-sort", JSON.stringify(sort)); }, [sort]);
    const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
    const COLUMNS = [
        { key: "date", label: "Pay / Date", align: "left", acc: (t) => t.transaction_date || "" },
        { key: "exdate", label: "Ex-date", align: "left", acc: (t) => t.ex_date || "" },
        { key: "type", label: "Type", align: "left", acc: (t) => t.transaction_type || "" },
        { key: "ticker", label: "Symbol", align: "left", acc: (t) => t.ticker || "" },
        { key: "sub", label: "Sub-portfolio", align: "left", acc: (t) => t.sub_portfolio || "" },
        { key: "qty", label: "Qty", align: "right", acc: (t) => t.quantity || 0 },
        { key: "price", label: "Price", align: "right", acc: (t) => t.price || 0 },
        { key: "fees", label: "Fees", align: "right", acc: (t) => t.fees || 0 },
        { key: "tax", label: "Tax", align: "right", acc: (t) => t.tax_withheld || 0 },
        { key: "taxpct", label: "% Tax", align: "right", acc: (t) => (t.transaction_type === "DIVIDEND" && t.quantity * t.price > 0) ? (t.tax_withheld || 0) / (t.quantity * t.price) * 100 : -1 },
        { key: "total", label: "Total", align: "right", acc: (t) => (t.quantity || 0) * (t.price || 0) },
    ];
    const TYPE_GROUPS = { TRADE: ["BUY", "SELL"], DIVIDEND: ["DIVIDEND"], CASH: ["DEPOSIT", "WITHDRAWAL"] };
    const sorted = useMemo(() => {
        if (!data) return [];
        const col = COLUMNS.find((c) => c.key === sort.key) || COLUMNS[0];
        const group = TYPE_GROUPS[filters.type];
        const arr = (group ? data.filter((t) => group.includes((t.transaction_type || "").toUpperCase())) : [...data]);
        arr.sort((a, b) => {
            const av = col.acc(a), bv = col.acc(b);
            if (typeof av === "string") { const c = av.localeCompare(bv); return sort.dir === "asc" ? c : -c; }
            return sort.dir === "asc" ? (av - bv) : (bv - av);
        });
        return arr;
    }, [data, sort, filters.type]);

    const confirmDelete = async () => {
        try { await api.delete(`/transactions/${deleting.id}`); toast.success("Transaction deleted"); refetch(); }
        catch (e) { toast.error(apiErr(e)); } finally { setDeleting(null); }
    };

    const exportCsv = async () => {
        const res = await api.get("/transactions/export", { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = "transactions.csv"; a.click(); URL.revokeObjectURL(url);
    };

    const openEdit = (t) => { setEditing(t); setDialogOpen(true); };
    const openAdd = () => { setEditing(null); setDialogOpen(true); };

    usePageMeta(data ? `${sorted.length} records` : "Loading…", undefined, dataUpdatedAt);
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 ml-auto">
                    <Button variant="outline" size="icon" onClick={() => setImportOpen(true)} data-testid="import-btn" title="Import CSV"><Upload className="w-4 h-4" /></Button>
                    <Button variant="outline" size="icon" onClick={exportCsv} data-testid="export-txns-btn" title="Export CSV"><Download className="w-4 h-4" /></Button>
                    <Button onClick={openAdd} data-testid="add-transaction-btn"><Plus className="w-4 h-4 mr-1.5" />Add</Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="p-3">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    <div className="relative col-span-2 md:col-span-2">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
                        <Input value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                            placeholder="Search ticker / notes" className="pl-8" data-testid="txn-search-input" />
                    </div>
                    <Select value={selectedSub} onValueChange={setSelectedSub}>
                        <SelectTrigger data-testid="txn-filter-sub"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All Sub-portfolios</SelectItem>
                            {subs.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={filters.type} onValueChange={(v) => setFilters((f) => ({ ...f, type: v }))}>
                        <SelectTrigger data-testid="txn-filter-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All Types</SelectItem>
                            <SelectItem value="TRADE">Buy / Sell</SelectItem>
                            <SelectItem value="DIVIDEND">Dividends</SelectItem>
                            <SelectItem value="CASH">Deposit / Withdrawal</SelectItem>
                        </SelectContent>
                    </Select>
                    <Input type="date" value={filters.start} onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))} className="num" data-testid="txn-filter-start" />
                    <Input type="date" value={filters.end} onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))} className="num" data-testid="txn-filter-end" />
                </div>
            </Card>

            {data && sorted.length > 0 && <PortfolioStrip cols={4} cells={txnStripCells(sorted, mask)} />}

            {isLoading ? <Skeleton className="h-96 rounded-xl" /> : sorted.length === 0 ? (
                (filters.ticker || filters.search || filters.start || filters.end || filters.type !== "ALL" || selectedSub !== "ALL")
                    ? <Card><EmptyState icon={ArrowLeftRight} title="No matching transactions" description="Nothing matches the current filters. Clear or change them to see more." testId="txns-no-results" /></Card>
                    : <Card><EmptyState icon={ArrowLeftRight} title="No transactions yet" description="Add your first transaction to build your portfolio."
                    actionLabel="Add transaction" onAction={openAdd} testId="txns-empty" /></Card>
            ) : (
                <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]" data-testid="transactions-table">
                            <thead className="bg-card/90 border-b border-border">
                                <tr className="text-xs uppercase tracking-wider text-muted-foreground">
                                    {COLUMNS.map((c) => (
                                        <th key={c.key} className={`px-4 py-3 ${c.align === "right" ? "text-right" : "text-left"}`}>
                                            <button onClick={() => toggleSort(c.key)} data-testid={`txn-sort-${c.key}`}
                                                className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${c.align === "right" ? "flex-row-reverse" : ""}`}>
                                                {c.label}
                                                {sort.key === c.key
                                                    ? (sort.dir === "asc"
                                                        ? <ChevronUp className="w-3 h-3 shrink-0 text-primary" data-testid={`txn-sort-asc-${c.key}`} />
                                                        : <ChevronDown className="w-3 h-3 shrink-0 text-primary" data-testid={`txn-sort-desc-${c.key}`} />)
                                                    : <ArrowUpDown className="w-3 h-3 shrink-0 opacity-30" />}
                                            </button>
                                        </th>
                                    ))}
                                    <th className="px-3 py-1.5 text-right"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((t) => (
                                    <tr key={t.id} className="border-b border-border/50 hover:bg-muted/40 transition-colors" data-testid={`txn-row-${t.id}`}>
                                        <td className="px-3 py-1.5 num whitespace-nowrap">{fmtDate(t.transaction_date)}</td>
                                        <td className="px-3 py-1.5 num whitespace-nowrap text-muted-foreground" data-testid={`txn-exdate-${t.id}`}>
                                            {t.transaction_type === "DIVIDEND" && t.ex_date ? (
                                                t.record_date ? (
                                                    <TooltipProvider delayDuration={100}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className="underline decoration-dotted decoration-muted-foreground/50 underline-offset-4 cursor-help">{fmtDate(t.ex_date)}</span>
                                                            </TooltipTrigger>
                                                            <TooltipContent data-testid={`txn-recorddate-${t.id}`}>Record date: {fmtDate(t.record_date)}</TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                ) : fmtDate(t.ex_date)
                                            ) : "—"}
                                        </td>
                                        <td className="px-3 py-1.5"><Badge variant="outline" className={`text-[10px] ${TXN_BADGE[t.transaction_type]}`}>{t.transaction_type}</Badge></td>
                                        <td className="px-3 py-1.5 font-semibold num">{t.ticker || "—"}</td>
                                        <td className="px-3 py-1.5 text-muted-foreground">{t.sub_portfolio === "__CASH__" ? "Cash (unallocated)" : t.sub_portfolio}</td>
                                        <td className="px-3 py-1.5 text-right num">{["DEPOSIT", "WITHDRAWAL"].includes(t.transaction_type) ? "—" : fmtNumber(t.quantity, 2)}</td>
                                        <td className="px-3 py-1.5 text-right num">{fmtCurrency(t.price, { decimals: 2 })}</td>
                                        <td className="px-3 py-1.5 text-right num text-muted-foreground">{t.transaction_type === "DIVIDEND" ? "—" : fmtCurrency(t.fees, { decimals: 2 })}</td>
                                        <td className="px-3 py-1.5 text-right num text-muted-foreground" data-testid={`txn-tax-${t.id}`}>{t.transaction_type === "DIVIDEND" ? fmtCurrency(t.tax_withheld || 0, { decimals: 2 }) : "—"}</td>
                                        <td className="px-3 py-1.5 text-right num text-muted-foreground" data-testid={`txn-taxpct-${t.id}`}>{t.transaction_type === "DIVIDEND" && (t.quantity * t.price) > 0 ? `${((t.tax_withheld || 0) / (t.quantity * t.price) * 100).toFixed(1)}%` : "—"}</td>
                                        <td className="px-3 py-1.5 text-right num font-medium">{mask(fmtCurrency((t.quantity || 0) * (t.price || 0)))}</td>
                                        <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                            {t.transaction_type !== "SPINOFF" && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)} data-testid={`txn-edit-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-loss" onClick={() => setDeleting(t)} data-testid={`txn-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            <TransactionDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={refetch} />
            <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => { refreshSubs(); refetch(); }} />

            <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                <AlertDialogContent data-testid="delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete transaction?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove the {deleting?.transaction_type} {deleting?.ticker || ""} record and recalculate your positions. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} className="bg-loss text-white hover:bg-loss/90" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
